import React, { FC, ReactElement, Ref, useEffect, useState } from 'react';
import { get } from 'lodash';

import { allSucceeded } from 'src/presentation/hooks/useResource';
import useFacebookLoginTempAuthToken from 'src/presentation/components/pages/social/hooks/useFacebookLoginTempAuthToken';

type FbSDKIframeLoaderProps = {
  iframeRef: Ref<any>;
  publicUrl: string;
};

type MetaErrorMessage = {
  error: string;
};

type WabaID = {
  waba_id: string;
};

const FBSKDIframeLoader: FC<FbSDKIframeLoaderProps> = ({ iframeRef, publicUrl }): ReactElement | null => {
  const { loadingState: tokenLoadingState, value: authToken } = useFacebookLoginTempAuthToken();
  if (!allSucceeded(tokenLoadingState) || !publicUrl) {
    return null;
  }
  const src = `${publicUrl}/api/facebook/v1/whatsapp-embedded-signup?userId=${authToken.userId}&token=${authToken.token}`;
  return <iframe ref={iframeRef} src={src} style={{ display: 'none', height: 0, width: 0 }} />;
};

export default FBSKDIframeLoader;

export function useEmbeddedSignupIframeListener({
  targetOrigin,
}): [
  WabaID | undefined,
  React.Dispatch<React.SetStateAction<WabaID | undefined>>,
  MetaErrorMessage | undefined,
  React.Dispatch<React.SetStateAction<MetaErrorMessage | undefined>>
] {
  const [wabaId, setWabaId] = useState<WabaID>();
  const [metaError, setMetaError] = useState<MetaErrorMessage>();
  useEffect(() => {
    const handler = event => {
      if (event.origin !== targetOrigin) {
        return;
      }
      const data = get(event, 'data.data');
      switch (event.data.message) {
        case 'wa_embedded_signup_completed':
          setWabaId(data);
          break;
        case 'error':
          setMetaError(data);
          break;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [targetOrigin]);
  return [wabaId, setWabaId, metaError, setMetaError];
}

export function initiateEmbeddedSignup({ iframeRef, targetOrigin, onlyWabaSharing }) {
  const current: any = get(iframeRef, 'current');
  if (targetOrigin && current) {
    const message = onlyWabaSharing
      ? 'wa_embedded_signup_initiated_only_waba_sharing'
      : 'wa_embedded_signup_initiated_no_limits';
    current.contentWindow.postMessage(message, targetOrigin);
  }
}
